import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { calendarsAPI, gcalAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { RefreshCw, Link2, Unlink, Settings2, Loader2, AlertTriangle } from 'lucide-react';

export const GoogleSyncPanel = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [cals, setCals] = useState(null);
  const [selected, setSelected] = useState([]);
  const [writeCal, setWriteCal] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const loadStatus = async () => {
    try {
      const s = await calendarsAPI.googleStatus();
      setStatus(s);
      setSelected(s.selected_calendars || []);
      setWriteCal(s.write_calendar_id || '');
    } catch { setStatus(null); }
  };
  useEffect(() => { loadStatus(); }, []);

  const loadCalendars = async () => {
    try { setCals(await calendarsAPI.googleCalendars()); }
    catch (e) { toast.error(e?.message || 'No se pudieron leer los calendarios'); setCals([]); }
  };
  useEffect(() => { if (open && status?.connected && cals === null) loadCalendars(); }, [open, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try { const { authorization_url } = await gcalAPI.login(); window.location.href = authorization_url; }
    catch (e) { toast.error(e?.message || 'No se pudo iniciar la conexión con Google'); }
  };
  const disconnect = async () => {
    if (!window.confirm('¿Desconectar Google Calendar? Los eventos importados dejarán de actualizarse.')) return;
    await gcalAPI.disconnect(); setCals(null); await loadStatus(); toast.success('Google Calendar desconectado');
  };
  const saveSettings = async () => {
    setBusy(true);
    try {
      const res = await calendarsAPI.googleSettings({ selected_calendars: selected, write_calendar_id: writeCal || null });
      toast.success(`Sincronizado: ${res.sync?.created ?? 0} nuevos, ${res.sync?.updated ?? 0} actualizados, ${res.pushed ?? 0} subidos a Google`);
      await loadStatus(); setOpen(false); onSynced?.();
    } catch (e) { toast.error(e?.message || 'No se pudo guardar'); }
    setBusy(false);
  };
  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await calendarsAPI.googleSyncNow();
      if (r.synced) toast.success(`Sincronizado: ${r.created} nuevos, ${r.updated} actualizados, ${r.deleted} eliminados`);
      else toast.info(r.reason === 'no_calendars' ? 'Elige qué calendarios sincronizar en Configurar' : 'Google no está conectado');
      await loadStatus(); onSynced?.();
    } catch (e) { toast.error(e?.message || 'Error al sincronizar'); }
    setBusy(false);
  };

  if (!status || !status.configured) return null;
  if (!status.connected) {
    const needScope = status.scope_error;
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="google-sync-panel">
        {needScope ? (
          <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1" data-testid="gcal-noscope-badge"><AlertTriangle className="w-3 h-3" /> Falta el permiso de Google Calendar</Badge>
        ) : status.auth_error ? (
          <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1" data-testid="gcal-expired-badge"><AlertTriangle className="w-3 h-3" /> Conexión caducada</Badge>
        ) : null}
        <Button variant="outline" size="sm" onClick={connect} data-testid="gcal-connect-btn"><Link2 className="w-4 h-4 mr-1" /> {(status.auth_error || needScope) ? 'Reconectar Google Calendar' : 'Conectar Google Calendar'}</Button>
        {needScope && <p className="w-full text-xs text-amber-700" data-testid="gcal-noscope-hint">Al reconectar, en la pantalla de Google marca la casilla «Ver, editar, compartir y eliminar permanentemente todos los calendarios» antes de pulsar Continuar.</p>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="google-sync-panel">
      <Badge className="bg-emerald-100 text-emerald-800 gap-1" data-testid="gcal-connected-badge">Google{status.email ? ` · ${status.email}` : ''}{status.selected_calendars?.length ? ` · ${status.selected_calendars.length} cal.` : ' · sin calendarios'}</Badge>
      <Button variant="outline" size="sm" onClick={syncNow} disabled={busy} data-testid="gcal-sync-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><Button variant="outline" size="sm" data-testid="gcal-settings-btn"><Settings2 className="w-4 h-4 mr-1" /> Configurar</Button></PopoverTrigger>
        <PopoverContent className="w-96 space-y-3" align="end" data-testid="gcal-settings-popover">
          <div>
            <p className="text-sm font-semibold">Calendarios de Google a sincronizar</p>
            <p className="text-xs text-muted-foreground">Solo lo que marques se importa al portal. Nada de esto lo ve el equipo.</p>
          </div>
          {cals === null ? <Loader2 className="w-4 h-4 animate-spin" /> : cals.length === 0 ? <p className="text-xs text-muted-foreground">No se encontraron calendarios.</p> : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {cals.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`gcal-cal-${c.id}`}>
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, c.id] : selected.filter((x) => x !== c.id))} />
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color || '#999' }} />
                  <span className="truncate">{c.summary}{c.primary ? ' (principal)' : ''}</span>
                  {c.access_role === 'reader' && <span className="text-[10px] text-muted-foreground">solo lectura</span>}
                </label>
              ))}
            </div>
          )}
          <div>
            <p className="text-xs font-medium mb-1">Calendario donde se crean los eventos del portal</p>
            <select value={writeCal} onChange={(e) => setWriteCal(e.target.value)} className="w-full border rounded-lg px-2 py-1.5 text-sm bg-background" data-testid="gcal-write-select">
              <option value="">— Elegir —</option>
              {(cals || []).filter((c) => c.access_role !== 'reader').map((c) => <option key={c.id} value={c.id}>{c.summary}</option>)}
            </select>
          </div>
          {status.last_sync && <p className="text-[11px] text-muted-foreground">Última sincronización: {new Date(status.last_sync).toLocaleString('es-ES')}</p>}
          <div className="flex items-center justify-between pt-1">
            <Button variant="ghost" size="sm" className="text-red-600" onClick={disconnect} data-testid="gcal-disconnect-btn"><Unlink className="w-4 h-4 mr-1" /> Desconectar</Button>
            <Button size="sm" onClick={saveSettings} disabled={busy} data-testid="gcal-save-settings-btn">{busy ? 'Guardando…' : 'Guardar y sincronizar'}</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
